'use client';

import React from 'react';
import { PortableText } from '@portabletext/react';
import InstitutionalImage from './InstitutionalImage';
import SmartTable from './SmartTable';

const components = {
  marks: {
    typography: ({ children, value }: any) => {
      const style: any = {};
      if (value.fontFamily) style.fontFamily = value.fontFamily;
      if (value.fontSize) style.fontSize = value.fontSize;
      if (value.color) style.color = value.color;
      if (value.highlight) style.backgroundColor = value.highlight;
      return <span style={style}>{children}</span>;
    },
    link: ({ children, value }: any) => {
      const rel = value.isAffiliate ? "sponsored nofollow" : "noopener noreferrer";
      return (
        <a href={value.href} className="text-brand hover:underline decoration-1 underline-offset-4 font-medium" target="_blank" rel={rel}>
          {children}
        </a>
      );
    },
    glossary: ({ children, value }: any) => {
      return (
        <span className="relative group inline-block cursor-help border-b border-dashed border-brand hover:bg-brand/5 transition-colors">
          <span className="font-medium">{children}</span>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl shadow-brand/20">
            <span className="font-bold block mb-1 uppercase tracking-wider text-brand">Definition</span>
            {value.definition}
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
          </span>
        </span>
      );
    },
  },
  types: {
    image: InstitutionalImage,
    tweetQuote: ({ value }: any) => {
      const articleUrl = typeof window !== 'undefined' ? window.location.href : '';
      const tweetText = `${value.quote}${value.author ? `\n— ${value.author}` : ''}\n\nFull Analysis: ${articleUrl}`;
      const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
      return (
        <div className="my-12 relative">
          <div className="absolute -left-3 md:-left-6 top-0 text-brand/20 font-serif text-6xl leading-none select-none">"</div>
          <div className="bg-gradient-to-br from-brand/5 to-transparent border-l-4 border-brand p-6 md:p-8 rounded-r-xl">
            <p className="font-serif text-xl md:text-3xl font-bold italic leading-snug mb-6 text-foreground">
              {value.quote}
            </p>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <span className="font-bold text-sm tracking-wider uppercase text-muted-foreground">
                {value.author ? `— ${value.author}` : ''}
              </span>
              <a 
                href={tweetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#000000] dark:bg-white dark:text-black text-white text-xs font-bold uppercase tracking-widest rounded-full hover:scale-105 transition-transform"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.004 3.985H5.078z"/></svg>
                Post Insight
              </a>
            </div>
          </div>
        </div>
      );
    },
    table: ({ value }: any) => <SmartTable value={value} />,
    relatedArticle: ({ value }: any) => {
      if (!value.article || !value.article.slug) return null;
      const customText = value.customText || "Deep Dive:";
      const targetSlug = value.article.slug.current;
      const targetTitle = value.article.title;
      return (
        <div className="my-10 bg-muted/20 border-l-4 border-brand p-6 sm:p-8 rounded-r-lg group relative overflow-hidden">
          <a href={`/post/${targetSlug}`} className="relative z-10 block">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-brand shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand">{customText}</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground group-hover:text-brand transition-colors leading-snug">
                {targetTitle}
              </h3>
            </div>
          </a>
        </div>
      );
    },
    youtubeEmbed: ({ value }: any) => {
      const url = value.url;
      if (!url) return null;
      let id = "";
      let isChannel = false;
      try {
        if (url.includes("v=")) {
          id = url.split("v=")[1].split("&")[0];
        } else if (url.includes("youtu.be/")) {
          id = url.split("youtu.be/")[1].split("?")[0];
        } else if (url.includes("/embed/")) {
          id = url.split("/embed/")[1].split("?")[0];
        } else if (url.includes("@") || url.includes("/channel/") || url.includes("/c/") || url.includes("/user/")) {
          isChannel = true;
          id = url.split("/").pop() || "";
        } else {
          const parts = url.split("/");
          id = parts[parts.length - 1];
        }
      } catch (e) {
        return <div className="p-4 bg-muted text-xs text-muted-foreground italic">Invalid Video URL</div>;
      }

      if (isChannel) {
        return (
          <div className="my-10 p-8 bg-brand/5 border border-brand/20 rounded-2xl flex flex-col items-center text-center gap-4 transition-all hover:bg-brand/10 group">
            <div className="w-16 h-16 rounded-full bg-brand flex items-center justify-center text-white shadow-xl mb-2 group-hover:scale-110 transition-transform">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
            </div>
            <h3 className="font-serif text-xl font-bold">{id.startsWith('@') ? id : `@${id}`}</h3>
            <p className="text-xs text-muted-foreground max-w-sm">Official WorldPrime Desk Channel • Institutional Intelligence Network</p>
            <a href={url} target="_blank" rel="noopener noreferrer" className="mt-2 px-6 py-2 bg-brand text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg hover:scale-105 transition-all">
              Launch Channel
            </a>
          </div>
        );
      }

      return (
        <div className="aspect-video w-full my-8 rounded-xl overflow-hidden shadow-2xl border border-white/10 bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${id}`}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    },
  },
  block: {
    h1: ({ children, value }: any) => (
      <h1 className="font-serif text-4xl font-black mt-16 mb-8 text-foreground" style={value?.backgroundColor ? { backgroundColor: value.backgroundColor, padding: '1.5rem', borderRadius: '4px' } : {}}>
        {children}
      </h1>
    ),
    h2: ({ children, value }: any) => (
      <h2 className="font-serif text-3xl font-bold mt-12 mb-6 text-foreground" style={value?.backgroundColor ? { backgroundColor: value.backgroundColor, padding: '1rem', borderRadius: '4px' } : {}}>
        {children}
      </h2>
    ),
    h3: ({ children, value }: any) => (
      <h3 className="font-serif text-2xl font-bold mt-10 mb-5 text-foreground/90" style={value?.backgroundColor ? { backgroundColor: value.backgroundColor, padding: '0.75rem', borderRadius: '4px' } : {}}>
        {children}
      </h3>
    ),
    h4: ({ children, value }: any) => (
      <h4 className="font-serif text-xl font-bold mt-8 mb-4 text-foreground/80 border-l-2 border-brand/30 pl-4" style={value?.backgroundColor ? { backgroundColor: value.backgroundColor, padding: '0.5rem', borderRadius: '4px' } : {}}>
        {children}
      </h4>
    ),
    normal: ({ children, value }: any) => (
      <p className="font-serif text-lg leading-relaxed text-muted-foreground mb-6" style={value?.backgroundColor ? { backgroundColor: value.backgroundColor, padding: '1.5rem', borderRadius: '4px' } : {}}>
        {children}
      </p>
    ),
    blockquote: ({ children, value }: any) => (
      <blockquote 
        className="border-l-4 border-brand pl-6 py-4 my-10 italic text-2xl font-serif text-muted-foreground bg-brand/5"
        style={value?.backgroundColor ? { backgroundColor: value.backgroundColor, padding: '1.5rem', borderRadius: '4px' } : {}}
      >
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }: any) => <ul className="list-disc pl-6 mb-6 space-y-2 text-muted-foreground font-serif">{children}</ul>,
  },
};

interface InstitutionalContentProps {
  content: any;
}

export default function InstitutionalContent({ content }: InstitutionalContentProps) {
  return (
    <div className="prose prose-zinc prose-lg max-w-none font-serif text-foreground leading-relaxed border-t border-border pt-12 transition-colors">
      <PortableText value={content} components={components} />
    </div>
  );
}
